import { useState, type ReactNode } from "react";
import type { Map as MapLibreMapInstance } from "maplibre-gl";
import {
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Eraser,
  Grid3x3,
  Hand,
  Lasso,
  Magnet,
  MapPin,
  Merge,
  Minus,
  MousePointer2,
  Pencil,
  Pentagon,
  Plus,
  Redo2,
  RotateCw,
  Ruler,
  Scissors,
  Search,
  Spline,
  SquareMinus,
  Trash2,
  Type,
  Undo2,
  Waypoints,
} from "lucide-react";
import {
  VISUALIZER_GRID_STEP_PRESETS,
  type VisualizerGridStepMeters,
} from "@/lib/visualizer-snap-grid";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useSearchServices } from "@/hooks/useSearchServices";
import { chromeSelectClass } from "@/lib/map-chrome";
import { flyToPlace, searchPlace } from "@/lib/place-search";
import { cn } from "@/lib/utils";

export type VisualizerToolMode =
  | "pan"
  | "select"
  | "point"
  | "line"
  | "polygon"
  | "text"
  | "freehand"
  | "rotate"
  | "eraser"
  | "lasso"
  | "vertex"
  | "scissors"
  | "measure";

/** Хоткеи режимов (без модификаторов). Не срабатывают в полях ввода. measure — без хоткея. */
export const VISUALIZER_TOOL_HOTKEYS: Partial<Record<VisualizerToolMode, string>> = {
  pan: "H",
  select: "V",
  point: "M",
  line: "L",
  polygon: "P",
  freehand: "D",
  text: "T",
  rotate: "R",
  eraser: "E",
  lasso: "W",
  vertex: "A",
  scissors: "X",
};

const STORAGE_KEY = "gpml.visualizer.toolbar.collapsed";

export type StrokeDashPreset = "solid" | "dash" | "dot" | "dash_dot";

const STROKE_DASH_OPTIONS: { value: StrokeDashPreset; label: string }[] = [
  { value: "solid", label: "Сплошная" },
  { value: "dash", label: "Штрих" },
  { value: "dot", label: "Точка" },
  { value: "dash_dot", label: "Штрих-точка" },
];

type StyleStripProps = {
  show: boolean;
  /** stroke — линия/площадь; text — цвет и размер шрифта */
  variant: "stroke" | "text";
  canManage: boolean;
  strokeColor: string;
  /** Цвет заливки площади (fill_color), независим от обводки */
  fillColor: string;
  strokeWidth: number;
  strokeDash: StrokeDashPreset;
  fillOpacity: number;
  showFill: boolean;
  fontSize: number;
  onStrokeColorChange: (value: string) => void;
  onFillColorChange: (value: string) => void;
  onStrokeWidthChange: (value: number) => void;
  onStrokeDashChange: (value: StrokeDashPreset) => void;
  onFillOpacityChange: (value: number) => void;
  onFontSizeChange: (value: number) => void;
  onSaveStyle: () => void;
  saving?: boolean;
};

type VisualizerToolbarProps = {
  mode: VisualizerToolMode;
  canManage: boolean;
  /** Точка/линия/площадь — нужна сцена и знак библиотеки */
  canDraw: boolean;
  /** Текст — достаточно открытой сцены */
  canPlaceText: boolean;
  canDelete: boolean;
  canCopy?: boolean;
  canMerge?: boolean;
  canDifference?: boolean;
  /** Правка вершин: ровно один незаблокированный line/polygon */
  canVertexEdit?: boolean;
  /** Ножницы: ровно один незаблокированный line/polygon */
  canScissors?: boolean;
  draftVertexCount: number;
  map: MapLibreMapInstance | null;
  styleStrip: StyleStripProps;
  canUndo?: boolean;
  canRedo?: boolean;
  historyBusy?: boolean;
  copyBusy?: boolean;
  mergeBusy?: boolean;
  differenceBusy?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onModeChange: (mode: VisualizerToolMode) => void;
  onDelete: () => void;
  onCopy?: () => void;
  onMerge?: () => void;
  onDifference?: () => void;
  /** Сетка поверх карты */
  gridEnabled?: boolean;
  /** Прилипание к сетке / вершинам */
  snapEnabled?: boolean;
  gridStepMeters?: VisualizerGridStepMeters;
  onGridEnabledChange?: (value: boolean) => void;
  onSnapEnabledChange?: (value: boolean) => void;
  onGridStepMetersChange?: (value: VisualizerGridStepMeters) => void;
};

type ToolDef = {
  id: VisualizerToolMode;
  label: string;
  hint: string;
  icon: typeof MousePointer2;
  group: "nav" | "draw" | "cad";
};

const tools: ToolDef[] = [
  {
    id: "select",
    label: "Выбор",
    hint: "Выделить знак на карте или в панели слоёв.",
    icon: MousePointer2,
    group: "nav",
  },
  {
    id: "pan",
    label: "Пан",
    hint: "Перемещать карту без постановки и выделения знаков.",
    icon: Hand,
    group: "nav",
  },
  {
    id: "measure",
    label: "Измерить",
    hint: "Клики — вершины линии; двойной клик — завершить; Esc — в выбор. Не сохраняется в сцену.",
    icon: Ruler,
    group: "nav",
  },
  {
    id: "point",
    label: "Точка",
    hint: "Один клик ставит точечный знак из библиотеки.",
    icon: MapPin,
    group: "draw",
  },
  {
    id: "line",
    label: "Линия",
    hint: "Клики набирают вершины. Enter или двойной клик — сохранить.",
    icon: Spline,
    group: "draw",
  },
  {
    id: "polygon",
    label: "Площадь",
    hint: "Клики набирают контур. Enter или двойной клик — сохранить.",
    icon: Pentagon,
    group: "draw",
  },
  {
    id: "freehand",
    label: "Свободная рука",
    hint: "Зажмите и ведите по карте; отпускание сохраняет линию.",
    icon: Pencil,
    group: "draw",
  },
  {
    id: "text",
    label: "Текст",
    hint: "Клик по карте — ввод текстовой аннотации без библиотеки.",
    icon: Type,
    group: "draw",
  },
  {
    id: "rotate",
    label: "Поворот",
    hint: "Повернуть один выбранный знак на карте (точка/текст — угол, линия/площадь — геометрия).",
    icon: RotateCw,
    group: "cad",
  },
  {
    id: "eraser",
    label: "Ластик",
    hint: "Клик по незаблокированному знаку удаляет его. Esc — в выбор.",
    icon: Eraser,
    group: "cad",
  },
  {
    id: "lasso",
    label: "Лассо",
    hint: "Обведите область — выделятся знаки внутри. Esc — отмена.",
    icon: Lasso,
    group: "cad",
  },
  {
    id: "vertex",
    label: "Вершины",
    hint: "Правка вершин линии/площади: перетащить, midpoint — вставить, Delete — удалить. Esc — в выбор.",
    icon: Waypoints,
    group: "cad",
  },
  {
    id: "scissors",
    label: "Ножницы",
    hint: "Разрезать один выделенный line/polygon режущей линией. Enter — разрезать, Esc — отмена.",
    icon: Scissors,
    group: "cad",
  },
];

function readCollapsed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function HotkeyBadge({ keys }: { keys: string }) {
  return (
    <span
      className="pointer-events-none absolute -bottom-0.5 -right-0.5 rounded-[2px] bg-muted px-0.5 text-[8px] font-semibold leading-none text-muted-foreground ring-1 ring-border"
      aria-hidden
    >
      {keys}
    </span>
  );
}

function ToolButton({
  tool,
  active,
  disabled,
  onActivate,
}: {
  tool: ToolDef;
  active: boolean;
  disabled: boolean;
  onActivate: () => void;
}) {
  const Icon = tool.icon;
  const hotkey = VISUALIZER_TOOL_HOTKEYS[tool.id];
  const title = hotkey ? `${tool.label} (${hotkey})` : tool.label;

  return (
    <HoverCard openDelay={280} closeDelay={80}>
      <HoverCardTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant={active ? "default" : "outline"}
          className="relative h-8 w-8"
          disabled={disabled}
          title={title}
          aria-label={title}
          aria-pressed={active}
          onClick={onActivate}
        >
          <Icon className="h-4 w-4" />
          {hotkey ? <HotkeyBadge keys={hotkey} /> : null}
        </Button>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="z-[120] w-[min(100vw-2rem,13.5rem)] px-3 py-2"
      >
        <p className="text-sm font-medium text-foreground">{tool.label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{tool.hint}</p>
        {hotkey ? (
          <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            Клавиша {hotkey}
          </p>
        ) : null}
      </HoverCardContent>
    </HoverCard>
  );
}

function GroupDivider() {
  return <div className="mx-0.5 h-7 w-px shrink-0 bg-border" aria-hidden />;
}

function Stepper({
  value,
  min,
  max,
  step,
  disabled,
  title,
  ariaLabel,
  onChange,
  format = (n) => String(n),
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  title: string;
  ariaLabel: string;
  onChange: (value: number) => void;
  format?: (n: number) => string;
}) {
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n * 100) / 100));
  return (
    <div className="flex items-center gap-0.5" title={title}>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        disabled={disabled || value <= min}
        aria-label={`Уменьшить: ${ariaLabel}`}
        onClick={() => onChange(clamp(value - step))}
      >
        <Minus className="h-3.5 w-3.5" />
      </Button>
      <span className="min-w-[1.75rem] text-center text-xs tabular-nums text-foreground">
        {format(value)}
      </span>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        disabled={disabled || value >= max}
        aria-label={`Увеличить: ${ariaLabel}`}
        onClick={() => onChange(clamp(value + step))}
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function ColorSwatch({
  value,
  disabled,
  title,
  ariaLabel,
  onChange,
}: {
  value: string;
  disabled: boolean;
  title: string;
  ariaLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <Input
      type="color"
      className="h-7 w-8 cursor-pointer border-0 bg-transparent p-0"
      value={value}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function ToolbarRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex flex-wrap items-center gap-1", className)}>{children}</div>;
}

export function VisualizerToolbar({
  mode,
  canManage,
  canDraw,
  canPlaceText,
  canDelete,
  canCopy = false,
  canMerge = false,
  canDifference = false,
  canVertexEdit = false,
  canScissors = false,
  draftVertexCount,
  map,
  styleStrip,
  canUndo = false,
  canRedo = false,
  historyBusy = false,
  copyBusy = false,
  mergeBusy = false,
  differenceBusy = false,
  onUndo,
  onRedo,
  onModeChange,
  onDelete,
  onCopy,
  onMerge,
  onDifference,
  gridEnabled = false,
  snapEnabled = false,
  gridStepMeters = 50,
  onGridEnabledChange,
  onSnapEnabledChange,
  onGridStepMetersChange,
}: VisualizerToolbarProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const { toast } = useToast();
  const { searchSettings } = useSearchServices();

  const setCollapsedPersist = (next: boolean) => {
    setCollapsed(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
    if (next) onModeChange("select");
  };

  const runSearch = async () => {
    if (!map) {
      toast({ title: "Карта ещё не готова", variant: "destructive" });
      return;
    }
    if (!query.trim()) {
      toast({ title: "Введите запрос", variant: "destructive" });
      return;
    }
    setSearching(true);
    try {
      const hit = await searchPlace(query, searchSettings);
      if (!hit) {
        toast({ title: "Место не найдено", description: "Попробуйте другой запрос", variant: "destructive" });
        return;
      }
      flyToPlace(map, hit);
      toast({ title: "Место найдено", description: hit.displayName });
    } catch (error) {
      toast({
        title: "Ошибка поиска",
        description: error instanceof Error ? error.message : "Не удалось выполнить поиск",
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  const isToolDisabled = (id: VisualizerToolMode) => {
    const libraryDraw = id === "point" || id === "line" || id === "polygon" || id === "freehand";
    if (id === "measure") return !canPlaceText;
    if (id === "vertex") return !canManage || !canPlaceText || !canVertexEdit;
    if (id === "scissors") return !canManage || !canPlaceText || !canScissors;
    if (id === "rotate" || id === "eraser" || id === "lasso") return !canManage || !canPlaceText;
    if (id === "text") return !canManage || !canPlaceText;
    if (libraryDraw) return !canManage || !canDraw;
    return false;
  };

  const activateTool = (id: VisualizerToolMode) => {
    if (mode === id && id !== "select" && id !== "pan") {
      onModeChange("select");
      return;
    }
    onModeChange(id);
  };

  const navTools = tools.filter((t) => t.group === "nav");
  const drawTools = tools.filter((t) => t.group === "draw");
  const cadTools = tools.filter((t) => t.group === "cad");

  if (collapsed) {
    return (
      <div
        className={cn(
          "pointer-events-auto absolute left-3 top-14 z-[110] flex flex-col items-center gap-1 rounded-md border bg-card/95 p-1 shadow-md backdrop-blur",
          chromeSelectClass,
        )}
      >
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="h-9 w-9"
          aria-label="Развернуть тулбар режимов"
          title="Развернуть тулбар"
          onClick={() => setCollapsedPersist(false)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
        <span
          className="px-0.5 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
          style={{ writingMode: "vertical-rl" }}
        >
          Режимы
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "pointer-events-auto absolute left-3 top-14 z-[110] flex max-w-[min(100%-1.5rem,44rem)] flex-col gap-1 rounded-md border bg-card/95 p-1.5 shadow-md backdrop-blur",
        chromeSelectClass,
      )}
    >
      <ToolbarRow>
        <div className="mr-0.5 flex items-center gap-0.5 px-0.5">
          <SectionHelpTooltip label="Справка: тулбар Визуализатора">
            <p>Выберите знак в панели «Слои», затем режим на тулбаре (кроме текста).</p>
            <p>
              Хоткеи: V — выбор, H — пан, M — точка, L — линия, P — площадь, D — свободная рука, T —
              текст, R — поворот, E — ластик, W — лассо, A — вершины, X — ножницы. Не работают в
              поле ввода.
            </p>
            <p>
              Пан — двигать карту. Точка — один клик. Линия и площадь — вершины; Enter или двойной
              клик. Свободная рука — зажать и вести. Текст — клик и ввод. Поворот — один выбранный
              знак. Ластик — клик удаляет. Лассо — обводка для мультивыбора. Вершины — перетащить
              узел. Ножницы — режущая линия по одному line/polygon, Enter — разрезать. Слияние —
              ≥2 площади. Вычитание — ровно 2 площади (первый минус второй). Измерить — клики
              по карте, двойной клик завершает линию; не сохраняется в сцену.
            </p>
            <p>
              Сетка — показать/скрыть сетку выбранного шага (м). Snap — прилипание кликов
              постановки и правки к узлам сетки (если сетка включена) и к вершинам знаков.
              Настройки запоминаются в браузере.
            </p>
            <p>
              Экспорт — кнопка «Экспорт» на карте (слева сверху): снимок текущего вида в PNG,
              JPEG, SVG или PDF. Не сохраняется в сцену.
            </p>
            <p>
              Стиль: для площади — независимые цвет заливки и цвет обводки, плюс прозрачность,
              толщина и штрих. Для линии — цвет, толщина и штрих обводки. OK сохраняет стиль.
            </p>
            <p>
              Отмена / повтор — Ctrl+Z / Ctrl+Y (или Ctrl+Shift+Z). Копировать — Ctrl+D (все
              выбранные незаблокированные; заблокированные пропускаются). Esc снимает режим (из
              пана — в выбор). Стрелки сворачивают тулбар.
            </p>
          </SectionHelpTooltip>
        </div>

        {styleStrip.show && (
          <>
            <div className="flex items-center gap-0.5 rounded border border-border/60 bg-muted/40 px-1 py-0.5">
              {styleStrip.variant === "text" ? (
                <>
                  <ColorSwatch
                    value={styleStrip.strokeColor}
                    disabled={!styleStrip.canManage}
                    title="Цвет текста"
                    ariaLabel="Цвет текста"
                    onChange={styleStrip.onStrokeColorChange}
                  />
                  <Stepper
                    value={styleStrip.fontSize}
                    min={10}
                    max={72}
                    step={1}
                    disabled={!styleStrip.canManage}
                    title="Размер шрифта"
                    ariaLabel="Размер шрифта"
                    onChange={styleStrip.onFontSizeChange}
                  />
                </>
              ) : (
                <>
                  {styleStrip.showFill && (
                    <>
                      <ColorSwatch
                        value={styleStrip.fillColor}
                        disabled={!styleStrip.canManage}
                        title="Цвет заливки"
                        ariaLabel="Цвет заливки"
                        onChange={styleStrip.onFillColorChange}
                      />
                      <Stepper
                        value={styleStrip.fillOpacity}
                        min={0}
                        max={1}
                        step={0.1}
                        disabled={!styleStrip.canManage}
                        title="Прозрачность заливки"
                        ariaLabel="Прозрачность заливки"
                        onChange={styleStrip.onFillOpacityChange}
                        format={(n) => `${Math.round(n * 100)}%`}
                      />
                      <GroupDivider />
                    </>
                  )}
                  <ColorSwatch
                    value={styleStrip.strokeColor}
                    disabled={!styleStrip.canManage}
                    title="Цвет обводки"
                    ariaLabel="Цвет обводки"
                    onChange={styleStrip.onStrokeColorChange}
                  />
                  <Stepper
                    value={styleStrip.strokeWidth}
                    min={1}
                    max={24}
                    step={1}
                    disabled={!styleStrip.canManage}
                    title="Толщина обводки"
                    ariaLabel="Толщина обводки"
                    onChange={styleStrip.onStrokeWidthChange}
                  />
                  <select
                    className={cn(chromeSelectClass, "h-7 max-w-[7.5rem] text-[11px]")}
                    value={styleStrip.strokeDash}
                    disabled={!styleStrip.canManage}
                    title="Штрих обводки"
                    aria-label="Штрих обводки"
                    onChange={(event) =>
                      styleStrip.onStrokeDashChange(event.target.value as StrokeDashPreset)
                    }
                  >
                    {STROKE_DASH_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="ml-0.5 h-7 px-2 text-xs"
                disabled={!styleStrip.canManage || styleStrip.saving}
                onClick={styleStrip.onSaveStyle}
              >
                OK
              </Button>
            </div>
            <GroupDivider />
          </>
        )}

        {navTools.map((tool) => (
          <ToolButton
            key={tool.id}
            tool={tool}
            active={mode === tool.id}
            disabled={isToolDisabled(tool.id)}
            onActivate={() => activateTool(tool.id)}
          />
        ))}
      </ToolbarRow>

      <ToolbarRow>
        <div className="relative min-w-[9rem] max-w-[14rem] flex-1 basis-[9rem]">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void runSearch();
              }
            }}
            disabled={searching || !map}
            placeholder="Поиск места"
            className="h-8 pl-7 text-sm"
            aria-label="Поиск места"
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-8 shrink-0 px-2"
          disabled={searching || !map}
          onClick={() => void runSearch()}
        >
          {searching ? "…" : "Найти"}
        </Button>

        <GroupDivider />

        <Button
          type="button"
          size="icon"
          variant="outline"
          className="relative h-8 w-8"
          disabled={!canManage || !canUndo || historyBusy || !onUndo}
          title="Отменить (Ctrl+Z)"
          aria-label="Отменить"
          onClick={() => onUndo?.()}
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="relative h-8 w-8"
          disabled={!canManage || !canRedo || historyBusy || !onRedo}
          title="Повторить (Ctrl+Y)"
          aria-label="Повторить"
          onClick={() => onRedo?.()}
        >
          <Redo2 className="h-4 w-4" />
        </Button>

        <GroupDivider />

        {drawTools.map((tool) => (
          <ToolButton
            key={tool.id}
            tool={tool}
            active={mode === tool.id}
            disabled={isToolDisabled(tool.id)}
            onActivate={() => activateTool(tool.id)}
          />
        ))}

        <GroupDivider />

        {cadTools.map((tool) => (
          <ToolButton
            key={tool.id}
            tool={tool}
            active={mode === tool.id}
            disabled={isToolDisabled(tool.id)}
            onActivate={() => activateTool(tool.id)}
          />
        ))}

        <GroupDivider />

        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          disabled={!canManage || !canCopy || copyBusy || !onCopy}
          title="Копировать выбранные (Ctrl+D)"
          aria-label="Копировать выбранные знаки"
          onClick={() => onCopy?.()}
        >
          <Copy className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          disabled={!canManage || !canDelete}
          title="Удалить выбранные знаки"
          aria-label="Удалить выбранные знаки"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          disabled={!canManage || !canMerge || mergeBusy || !onMerge}
          title="Слить площади"
          aria-label="Слить площади"
          onClick={() => onMerge?.()}
        >
          <Merge className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          disabled={!canManage || !canDifference || differenceBusy || !onDifference}
          title="Вычесть площади (первый − второй)"
          aria-label="Вычесть площади"
          onClick={() => onDifference?.()}
        >
          <SquareMinus className="h-4 w-4" />
        </Button>

        <GroupDivider />

        <Button
          type="button"
          size="icon"
          variant={gridEnabled ? "default" : "outline"}
          className="h-8 w-8"
          title={gridEnabled ? "Скрыть сетку" : "Показать сетку"}
          aria-label={gridEnabled ? "Скрыть сетку" : "Показать сетку"}
          aria-pressed={gridEnabled}
          onClick={() => onGridEnabledChange?.(!gridEnabled)}
        >
          <Grid3x3 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant={snapEnabled ? "default" : "outline"}
          className="h-8 w-8"
          disabled={!canManage}
          title={
            canManage
              ? snapEnabled
                ? "Выключить snap"
                : "Включить snap (сетка и вершины)"
              : "Snap доступен при праве управления"
          }
          aria-label={snapEnabled ? "Выключить snap" : "Включить snap"}
          aria-pressed={snapEnabled}
          onClick={() => onSnapEnabledChange?.(!snapEnabled)}
        >
          <Magnet className="h-4 w-4" />
        </Button>
        <select
          className={cn(chromeSelectClass, "h-8 max-w-[5.5rem] text-[11px]")}
          value={gridStepMeters}
          title="Шаг сетки"
          aria-label="Шаг сетки в метрах"
          onChange={(event) =>
            onGridStepMetersChange?.(Number(event.target.value) as VisualizerGridStepMeters)
          }
        >
          {VISUALIZER_GRID_STEP_PRESETS.map((step) => (
            <option key={step} value={step}>
              {step} м
            </option>
          ))}
        </select>

        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="ml-auto h-8 w-8"
          aria-label="Свернуть тулбар режимов"
          title="Свернуть тулбар"
          onClick={() => setCollapsedPersist(true)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
      </ToolbarRow>

      {(mode === "line" || mode === "polygon" || mode === "scissors") && (
        <p className="px-1 text-xs text-muted-foreground">
          Вершин: {draftVertexCount}. Enter —{" "}
          {mode === "scissors" ? "разрезать" : "сохранить"}, Backspace — убрать, Esc — отмена.
        </p>
      )}
      {mode === "freehand" && (
        <p className="px-1 text-xs text-muted-foreground">
          Зажмите и ведите по карте. Отпускание сохраняет линию. Esc — отмена.
          {draftVertexCount > 0 ? ` Точек: ${draftVertexCount}.` : ""}
        </p>
      )}
      {mode === "lasso" && (
        <p className="px-1 text-xs text-muted-foreground">
          Зажмите и обведите область. Отпускание выделяет знаки внутри. Esc — отмена.
        </p>
      )}
      {mode === "rotate" && (
        <p className="px-1 text-xs text-muted-foreground">
          Выберите один знак и перетащите вокруг него для поворота. Esc — в выбор.
        </p>
      )}
      {mode === "eraser" && (
        <p className="px-1 text-xs text-muted-foreground">
          Клик по незаблокированному знаку удаляет его. Esc — в выбор.
        </p>
      )}
      {mode === "vertex" && (
        <p className="px-1 text-xs text-muted-foreground">
          Перетащите вершину; midpoint — вставить; клик по вершине и Delete — удалить. Esc — в
          выбор.
        </p>
      )}
      {mode === "measure" && (
        <p className="px-1 text-xs text-muted-foreground">
          Клики ставят вершины; двойной клик — завершить линию. Esc — в выбор. Не сохраняется в
          сцену.
        </p>
      )}
    </div>
  );
}
